import type { FC } from 'react';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useUserStore } from '@Auth/store';
import { Show } from '@components/Show';
import { PUBLISHING_OPTIONS_DROPDOWN_Z_INDEX } from '@constants/z-indices';
import type { ResourceState } from '@domain/schemas/contract.schema';
import { Label, Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react';
import { DocumentMode } from '@helpers/document-mode';
import { getDocumentMode } from '@helpers/getDocumentMode';
import { filterPublishingOptions, isCurrentStatus } from '@helpers/status';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/solid';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { useModalDispatch } from '@hooks/useModalProvider';
import { isAdmin, isSuperadmin } from '@root/helpers/permissions';
import { MODALS } from '@src/constants';
import sharedClasses from '@src/utils/shared-classes';
import clsx from 'clsx';

import type { PublishingOptionType } from '../../types';
import { getTransitionConfig } from '../Modal/ConfirmStatusModal/helpers/getTransitionConfig';

import { getIconForStatus, getShouldShowAdminOnlyBadge } from './helpers';

type PublishingOptionsPropsType = {
  modal?: MODALS;
  status?: ResourceState;
  hasMaterialChanges?: boolean;
  isDisabled: boolean;
  isLoading?: boolean;
  onStatusUpdate: (status: ResourceState, shouldBypassDatapointValidation?: boolean) => Promise<void>;
  publishingOptions: PublishingOptionType[];
};

const PublishingOptions: FC<PublishingOptionsPropsType> = ({
  status,
  hasMaterialChanges,
  isDisabled,
  isLoading = false,
  onStatusUpdate,
  publishingOptions,
  modal = MODALS.CONFIRM_STATUS_FINAL,
}) => {
  const getFeatureFlagState = useFeatureFlags();
  const isFirmOrderEnabled = getFeatureFlagState('FirmOrderStatus');
  const isTemplateReviewWorkflowEnabled = getFeatureFlagState('TemplateReviewWorkflow');
  const isEndorsementReviewEnabled = getFeatureFlagState('EndorsementReview');

  const { mode } = getDocumentMode();

  const user = useUserStore(({ user }) => user);
  const isAdminUser = isAdmin(user);
  const isSuperadminUser = isSuperadmin(user);

  const [selectedPublishingOption, setSelectedPublishingOption] = useState(
    publishingOptions.find((option) => option.id === status) ?? publishingOptions[0],
  );
  const { showModal } = useModalDispatch();

  const filteredPublishingOptions = useMemo(
    () =>
      filterPublishingOptions({
        documentMode: mode,
        isAdmin: isAdminUser,
        isEndorsementReviewEnabled,
        isFirmOrderEnabled,
        isSuperadmin: isSuperadminUser,
        isTemplateReviewWorkflowEnabled,
        publishingOptions,
        status,
      }),
    [
      publishingOptions,
      status,
      isFirmOrderEnabled,
      isAdminUser,
      mode,
      isSuperadminUser,
      isTemplateReviewWorkflowEnabled,
      isEndorsementReviewEnabled,
    ],
  );

  useEffect(() => {
    setSelectedPublishingOption(
      filteredPublishingOptions.find(({ id }) => id === status) ?? filteredPublishingOptions[0],
    );
  }, [filteredPublishingOptions, status]);

  const handleStatusUpdate = useCallback(
    (id: ResourceState, shouldBypassDatapointValidation?: boolean) =>
      onStatusUpdate(id, shouldBypassDatapointValidation),
    [onStatusUpdate],
  );

  const handleOpenModal = (publishingOption: PublishingOptionType) =>
    showModal(modal, {
      onConfirm: () => handleStatusUpdate(publishingOption.id),
      status: publishingOption.id,
    });

  const handleVisibilityStatusUpdate = async (publishingOption: PublishingOptionType) => {
    if (isCurrentStatus(status, publishingOption.id)) {
      return;
    }

    const { mode } = getDocumentMode();

    const needsValidationWarning =
      (publishingOption.id === 'FIRM_ORDER' && status === 'REVIEW' && mode === DocumentMode.SUBMISSIONS) ||
      (publishingOption.id === 'FINAL' && mode === DocumentMode.ENDORSEMENTS && hasMaterialChanges === false);

    if (needsValidationWarning) {
      showModal(MODALS.CONFIRM_STATUS_FIRM_ORDER, {
        onConfirm: () => handleStatusUpdate(publishingOption.id),
        onConfirmByPassValidation: () => handleStatusUpdate(publishingOption.id, true),
        targetStatus: publishingOption.id,
      });
      return;
    }
    const config = getTransitionConfig(status, publishingOption.id, mode);

    if (config) {
      handleOpenModal(publishingOption);
    } else {
      setSelectedPublishingOption(publishingOption);
      await onStatusUpdate(publishingOption.id);
    }
  };

  return (
    <Listbox disabled={isDisabled} value={selectedPublishingOption} onChange={handleVisibilityStatusUpdate}>
      {({ open }) => (
        <>
          <Label className="sr-only" data-testid="PublishingOptions:Status">
            Change published status
          </Label>
          <div className="relative">
            <div className="divide-primary-600 inline-flex divide-x rounded-md shadow-xs">
              <div className="divide-primary-600 relative z-0 inline-flex divide-x rounded-md shadow-xs">
                <div
                  className={clsx(
                    isDisabled && sharedClasses.disabled,
                    'bg-primary-500 relative inline-flex items-center rounded-l-md border border-transparent py-1 pr-4 pl-3 text-white shadow-xs',
                  )}
                >
                  {getIconForStatus(selectedPublishingOption.id)}
                  <p data-testid={selectedPublishingOption.title} className="ml-2.5 text-sm font-medium">
                    {selectedPublishingOption.title}
                  </p>
                </div>
                <ListboxButton
                  aria-label="Change published status"
                  aria-busy={isLoading}
                  data-testid="PublishingOptions:Button"
                  className={clsx(
                    'bg-primary-500 hover:bg-primary-600 focus:ring-primary-500 focus:ring-offset-info-50 relative inline-flex h-9 items-center rounded-l-none rounded-r-md p-2 py-1 text-sm font-medium text-white focus:z-10 focus:ring-2 focus:ring-offset-2 focus:outline-hidden',
                    isDisabled && sharedClasses.disabled,
                  )}
                >
                  <span className="sr-only">Change published status</span>
                  <ChevronDownIcon className="h-5 w-5 text-white" aria-hidden="true" />
                </ListboxButton>
              </div>
            </div>
            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <ListboxOptions
                className={clsx(
                  'divide-info-200 absolute right-0 mt-2 w-72 origin-top-right divide-y overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-hidden',
                  PUBLISHING_OPTIONS_DROPDOWN_Z_INDEX,
                )}
              >
                {filteredPublishingOptions.map((option) => (
                  <ListboxOption
                    key={option.title}
                    className={({ focus }) =>
                      clsx(
                        isCurrentStatus(option.id) ? 'cursor-not-allowed' : '',
                        focus ? 'bg-primary-500 text-white' : 'text-info-900',
                        'relative cursor-default p-4 text-sm select-none',
                      )
                    }
                    value={option}
                    data-testid={`PublishingOption:${option.title}`}
                  >
                    {({ selected, focus }) => (
                      <div className="flex flex-col" data-testid={`PublishingOption:${option.title}`}>
                        <div className="flex justify-between">
                          <div className="flex items-center gap-2">
                            <p className={selected ? 'font-semibold' : 'font-normal'}>{option.title}</p>
                          </div>
                          <Show when={getShouldShowAdminOnlyBadge(status, selected)}>
                            <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                              Admin Only
                            </span>
                          </Show>
                          {selected ? (
                            <span className={focus ? 'text-white' : 'text-primary-500'}>
                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                            </span>
                          ) : null}
                        </div>
                        <p className={clsx(focus ? 'text-primary-200' : 'text-info-500', 'mt-2')}>
                          {option.description}
                        </p>
                      </div>
                    )}
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </Transition>
          </div>
        </>
      )}
    </Listbox>
  );
};

export default PublishingOptions;
